import React, {
  memo,
  FC,
  useMemo,
  useCallback,
  useState,
  ChangeEvent,
}                            from 'react';
import { History }           from 'history';
import {
  TDataTableRow,
  TSelectOnChange,
}                            from '@typings/components';
import { IBid }              from '@typings/auction';
import {
  IComplaint,
  IBidderUser,
}                            from '@typings/registeredBidders';
import {
  IComment,
  ICommentValues,
}                            from '@typings/comments';
import {
  EAppPage,
  EUserStatus,
  ECommentType,
}                            from '@enums/common';
import { EBiddersPage }      from '@enums/registeredBidders';
import { EAccountPage }      from '@enums/accountManagement';
import {
  formatCurrency,
  formatTitle,
  getFormattedDate,
}                            from '@utils/commonFunctions';
import {
  useFetchComments,
  useFetchDocument,
  useSaveComment,
}                            from '@modules/comments/queries/commentsQueries';
import {
  useFetchBidderInfo,
  useFetchBidderActivity,
  useFetchBidderComplaints,
  useUpdateBidderPassword,
  useChangeUserStatus,
}                            from '@modules/registeredBidders/queries/registeredBiddersQueries';
import { Loader }            from '@components/Loader/Loader';
import { BidderDetailsPage } from '@modules/registeredBidders/pages/BidderDetailsPage';

const getBidsTableRows = (bids: IBid[] = []): TDataTableRow[] => bids.map((bid) => [
  { value: formatCurrency(bid.amount) },
  { value: formatTitle(bid) },
  { value: getFormattedDate(bid.dateCreated, 'LLL') },
]);

const getComplaintsTableRows = (
  complaints: IComplaint[] = [],
): TDataTableRow[] => complaints.map((complaint) => [
  { value: getFormattedDate(complaint.dateCreated, 'MM/DD/YY') },
  { value: complaint.auctionFullName },
  {
    value : complaint.creditUnionName,
    link  : `${EAppPage.AccountManagement}/${complaint.fromCuid}/${EAccountPage.Edit}`,
  },
]);

interface IProps extends Pick<History, 'push'>{
  id: string;
}

const BidderDetails: FC<IProps> = ({ id, push }) => {
  const [comments, setComments]     = useState<IComment[]>([]);
  const [bidderInfo, setBidderInfo] = useState<IBidderUser | null>(null);

  // #region API INTEGRATION
  useFetchBidderInfo(id, {
    onSuccess: (data) => setBidderInfo(data),
  });

  useFetchComments({
    type: ECommentType.Bidder,
    id,
  }, {
    onSuccess: (data) => {
      setComments(data);
    },
  });

  const { mutateAsync: saveComment } = useSaveComment({
    onSuccess: (data) => {
      setComments((prev) => [data, ...prev]);
    },
  });

  const { data: bidsList } = useFetchBidderActivity(id);

  const { data: complaintsList } = useFetchBidderComplaints(id);

  const {
    mutateAsync : updateBidderPassword,
    isLoading   : isPasswordUpdating,
  } = useUpdateBidderPassword({
    onSuccess: () => {
      push(`${EAppPage.RegisteredBidders}/${EBiddersPage.Search}`);
    },
  });

  const { mutateAsync: fetchDocument } = useFetchDocument();

  const { mutateAsync: changeBidderStatus } = useChangeUserStatus();
  // #endregion

  const onStatusChange = useCallback<TSelectOnChange>(
    async (event: ChangeEvent<HTMLSelectElement>): Promise<void> => {
      const status: EUserStatus = event.target.value as unknown as EUserStatus;

      await changeBidderStatus({ status, id });
      setBidderInfo((prev) => (prev
        ? { ...prev, status }
        : null));
    },
    [changeBidderStatus, id],
  );

  const onSaveComment = useCallback(
    async (comment: ICommentValues): Promise<void> => {
      await saveComment({
        type: ECommentType.Bidder,
        comment,
        id,
      });
    },
    [id, saveComment],
  );

  const bids = useMemo<TDataTableRow[]>(
    () => getBidsTableRows(bidsList),
    [bidsList],
  );

  const complaints = useMemo<TDataTableRow[]>(
    () => getComplaintsTableRows(complaintsList),
    [complaintsList],
  );

  if (!bidderInfo || !bidsList || !complaintsList || !comments) {
    return <Loader />;
  }

  return (
    <BidderDetailsPage
      isPasswordUpdating   = {isPasswordUpdating}
      user                 = {bidderInfo}
      comments             = {comments}
      bids                 = {bids}
      complaints           = {complaints}
      fetchDocument        = {fetchDocument}
      handleSaveComment    = {onSaveComment}
      handleChangeStatus   = {onStatusChange}
      handleClickReturn    = {() => push(`${EAppPage.RegisteredBidders}/${EBiddersPage.Search}`)}
      updateBidderPassword = {updateBidderPassword}
    />
  );
};

export const BidderDetailsContainer = memo(BidderDetails);
